import { differenceInYears, format, isSameDay, parseISO, subDays } from 'date-fns';

import { ClinicalAllergy, ClinicalCondition, ClinicalMedication } from '../types';

export const EMPTY_VALUE = '—';

const formatIso = (value: string | null | undefined, pattern: string) => {
    if (!value) {
        return EMPTY_VALUE;
    }

    const parsed = parseISO(value);

    return Number.isNaN(parsed.getTime()) ? EMPTY_VALUE : format(parsed, pattern);
};

export const formatDate = (value: string | null | undefined) => formatIso(value, 'MMM dd, yyyy');

export const formatDateTime = (value: string | null | undefined) =>
    formatIso(value, 'MMM dd, yyyy · h:mm a');

/**
 * Newest `last_synced_at` across a block's records. An empty block falls back to
 * the record-level stamp, which still says when DrChrono was last checked.
 */
export const getLastSyncedAt = (
    records: { last_synced_at: string | null }[],
    fallback: string | null
) =>
    records.reduce<string | null>((latest, { last_synced_at }) => {
        if (!last_synced_at) {
            return latest;
        }

        return !latest || parseISO(last_synced_at) > parseISO(latest) ? last_synced_at : latest;
    }, null) ?? fallback;

/** `Last synced today 7:42 AM`, `… yesterday 6:00 PM`, else `Last synced Sep 17, 2026`. */
export const formatLastSynced = (value: string | null | undefined, now: Date = new Date()) => {
    if (!value) {
        return undefined;
    }

    const parsed = parseISO(value);

    if (Number.isNaN(parsed.getTime())) {
        return undefined;
    }

    if (isSameDay(parsed, now)) {
        return `Last synced today ${format(parsed, 'h:mm a')}`;
    }

    if (isSameDay(parsed, subDays(now, 1))) {
        return `Last synced yesterday ${format(parsed, 'h:mm a')}`;
    }

    return `Last synced ${format(parsed, 'MMM dd, yyyy')}`;
};

/**
 * `1938-11-04` -> `Nov 04, 1938 (87)`. Only the calendar date is read: a DOB sent
 * as UTC midnight would otherwise render as the previous day west of Greenwich.
 */
export const formatDateOfBirth = (value: string | null | undefined, now: Date = new Date()) => {
    if (!value) {
        return EMPTY_VALUE;
    }

    const parsed = parseISO(value.slice(0, 10));

    return Number.isNaN(parsed.getTime())
        ? EMPTY_VALUE
        : `${format(parsed, 'MMM dd, yyyy')} (${differenceInYears(now, parsed)})`;
};

/** `not_administered` -> `Not administered`; used to render pass-through API strings. */
export const formatEnumLabel = (value: string | null | undefined) => {
    if (!value) {
        return EMPTY_VALUE;
    }

    const spaced = value.replace(/_/g, ' ');

    return `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`;
};

/** `I10` + version `10` -> `ICD-10 I10`, falling back to the SNOMED CT code. */
export const formatConditionCode = (condition: ClinicalCondition) => {
    if (condition.icd_code) {
        return condition.icd_version
            ? `ICD-${condition.icd_version} ${condition.icd_code}`
            : condition.icd_code;
    }

    return condition.snomed_ct_code ? `SNOMED ${condition.snomed_ct_code}` : EMPTY_VALUE;
};

/** RxNorm is the primary med code; NDC is shown only when RxNorm is absent. */
export const formatMedicationCode = (medication: ClinicalMedication) => {
    if (medication.rxnorm) {
        return `RxNorm ${medication.rxnorm}`;
    }

    return medication.ndc ? `NDC ${medication.ndc}` : EMPTY_VALUE;
};

/** The allergen's description, else its RxNorm code — DrChrono often sends only the code. */
export const formatAllergyName = (allergy: ClinicalAllergy) => {
    if (allergy.description?.trim()) {
        return allergy.description;
    }

    return allergy.rxnorm ? `RxNorm ${allergy.rxnorm}` : 'Unnamed';
};

/** `flagged_by` / `changed_by` are bare hero_user_ids; the API sends no name. */
export const formatUserRef = (userId: number | null | undefined) =>
    userId ? `User #${userId}` : 'System';
